import { useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Btn } from "@/components/Btn";
import { ColumnsButton } from "@/components/ColumnsButton";
import {
  allowedColumnsForTab,
  loadColumnWidths,
  loadVisibleColumns,
  resolvePresetColumns,
  saveColumnWidths,
  saveVisibleColumns,
  type ColumnKey,
} from "@/lib/columns-config";
import { I } from "@/components/Icon";
import { UserAlertsBell } from "@/components/UserAlertsBell";
import { MappingGateBanner } from "@/components/MappingGateBanner";
import type { EntityItem } from "@/components/MultiEntityPicker";
import { Pager } from "@/components/Pager";
import { DateRangePicker } from "@/components/DateRangePicker";
import { SegRange } from "@/components/SegRange";
import { SingleEntityPicker } from "@/components/SingleEntityPicker";
import { StatsTable } from "@/components/StatsTable";
import { Topbar } from "@/components/Topbar";
import {
  adsFiltersToStatsFilters,
  useAdsFilters,
} from "@/lib/ads-filters";
import {
  useApps,
  useAppCohortConfig,
  useBulkApplyCpp,
  useBulkDuplicateCampaign,
  useBulkEditBidBudget,
  useCampaignsTable,
  useColumnPresets,
  useCustomProductPages,
  useDefaultApp,
  useLabels,
  useReadOnlyCampaigns,
  useRefreshAppsFromAsa,
  useSetDefaultApp,
  useUpdateCampaign,
} from "@/lib/api-hooks";
import { ApplyCppModal, type CppOption } from "@/components/ApplyCppModal";
import { BulkActions } from "@/components/BulkActions";
import { EntityChartPanel } from "@/components/EntityChartPanel";
import { EditBidBudgetModal } from "@/components/EditBidBudgetModal";
import { RenameCampaignModal } from "@/components/RenameCampaignModal";
import { RulesManagerModal } from "@/components/RulesManagerModal";
import { DuplicateCampaignModal } from "@/components/DuplicateCampaignModal";
import { FilterMenuButton } from "@/components/FilterMenuButton";
import { AdsFilterProvider } from "@/components/ads-filter/AdsFilterContext";
import { useAdsFilter } from "@/components/ads-filter/context";
import { FilterChips } from "@/components/ads-filter/FilterChips";
import { FilterEmptyState } from "@/components/ads-filter/FilterEmptyState";
import { useChipsPlacement } from "@/components/ads-filter/useChipsPlacement";
import { useOrgPublicId } from "@/lib/useOrgNav";
import { LabelsProvider } from "@/components/LabelsProvider";
import { StatusToast } from "@/components/StatusToast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { reportBulkAction } from "@/lib/sentry-breadcrumbs";
import {
  awaitPendingWrites,
  runWithAsaConfirmToast,
} from "@/lib/pending-writes-tracker";
import { useAuth, useCanWriteApp } from "@/lib/useAuth";
import { useOrgNavigate } from "@/lib/useOrgNav";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { fmtInt } from "@/lib/fmt";
import type { CampaignStatsRow } from "@/lib/types";
import { CampaignStudioProgressBar } from "@/components/CampaignStudioProgressBar";

// Sticky search + status filter for the Campaigns list — survive
// navigating into a campaign and back (the page remounts) until the
// user changes them.
const STATUS_LS_KEY = "oneads.ads_manager.status.campaigns";
const SEARCH_LS_KEY = "oneads.ads_manager.search.campaigns";

/**
 * /ads — the Ads Manager root. Lists every campaign in the current
 * ASA account. Row click navigates to the campaign detail page
 * (tabbed: Ad Groups / Keywords / Negatives / Search Terms / Ads).
 *
 * Filters (app + period) live in the URL so back-navigation and
 * sharing both work; search + status are client-side, persisted in
 * localStorage so they stick across navigation until the user changes
 * them.
 *
 * Checkbox selection drives the bulk-action bar (pause / enable /
 * delete via ASA push-on-write). In v1.0 only the UI is wired —
 * hooking the mutations comes with the follow-on that adds per-
 * resource bulk endpoints.
 */
/** The filter's state lives per account × app (TZ §8); the provider wraps
 *  the page so the funnel in the top strip, the chips in the toolbar and
 *  the table's query all read one applied filter. */
export function CampaignsPage() {
  const [filters] = useAdsFilters();
  const labelsQuery = useLabels();
  const accountId = useOrgPublicId() ?? "0";
  return (
    <AdsFilterProvider
      grain="campaigns"
      accountId={accountId}
      appId={filters.appId}
      labels={labelsQuery.data ?? []}
    >
      <CampaignsPageInner />
    </AdsFilterProvider>
  );
}

function CampaignsPageInner() {
  const adsFilter = useAdsFilter();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const chipsWrapped = useChipsPlacement(toolbarRef, chipsRef, adsFilter.showChips);
  const navigate = useOrgNavigate();
  const { user } = useAuth();

  const [filters, setFilters] = useAdsFilters();
  // ★ Открыт ли график — состояние СТРАНИЦЫ, а не панели действий:
  // панель исчезает, когда снимают выделение, а график по ТЗ обязан
  // остаться и показать placeholder. Живи флаг в ней — он бы пропадал
  // вместе с ней ровно в тот момент, когда выделение перекладывают.
  const [chartOpen, setChartOpen] = useState(false);
  const appsQuery = useApps();
  // Per-app edit gate (§51): editable only for a picked app the user can write
  // (ads_manager|admin). "All apps" → coarse can-write-somewhere; the backend
  // still enforces the specific target app per request.
  const canEdit = useCanWriteApp(
    typeof filters.appId === "number" ? filters.appId : null,
  );
  // Кампании групп Apple с доступом только на чтение: правки по ним
  // выключены (ТЗ apple_signin_connect §10). Обычно множество пустое.
  const readOnlyCampaigns = useReadOnlyCampaigns().data;

  // Server-backed default-app resolution (per user 2026-05-16).
  // Resolution chain (user sticky → org cache → 12mo fallback →
  // all_apps / no_apps) lives in `app.services.default_app_service`.
  //
  // Re-resolves on org switch — React Router doesn't remount this
  // page when only `:orgPublicId` changes, so we track the current
  // org and reset state whenever it shifts. Without this, after
  // switching from CTMOBI to APPLAYERS the page would still carry
  // CTMOBI's `?app=<id>` URL param + stale "fired" guard and never
  // pick APPLAYERS's default (#26 follow-up 2026-05-17).
  const orgId = user?.org_id ?? null;
  const defaultAppQuery = useDefaultApp({ enabled: true, orgId });
  const setDefaultApp = useSetDefaultApp();

  // (org_id, fired) tuple — reset to false whenever orgId changes
  // so the first effect in the new org re-applies the default.
  const lastOrgRef = useRef<number | null>(null);
  const defaultAppFiredRef = useRef(false);
  // Persist the sticky app ONLY on an explicit picker change — never on
  // the auto-applied default. Auto-persisting froze the default-app
  // (the sticky then wins the resolver chain), so the "top-7d spender"
  // default never re-evaluated. The sticky is shared with Overview, so
  // both pages must follow this rule. (User report 2026-06-03.)
  const userPickedAppRef = useRef(false);
  if (lastOrgRef.current !== orgId) {
    lastOrgRef.current = orgId;
    defaultAppFiredRef.current = false;
  }

  // Treat the URL's `?app=` param as authoritative ONLY if its value
  // is a valid app for the CURRENT org. After org switch the URL
  // typically still carries the previous org's app_id — that's a
  // stale param, not the user's intent for this org. We validate
  // against the freshly-loaded apps list and treat invalid values
  // as if the param weren't there.
  const urlAppParam = (() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("app");
    if (raw === null) return null;
    if (raw === "all") return "all" as const;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  // `null` = no decision yet (apps loading), `true` = valid, `false`
  // = stale/invalid → fall through to default-app resolution.
  const urlAppIsValidForOrg: boolean | null = (() => {
    if (urlAppParam === null) return false;
    if (urlAppParam === "all") return true;
    if (!appsQuery.data) return null;
    return appsQuery.data.some((a) => a.id === urlAppParam);
  })();

  useEffect(() => {
    if (defaultAppFiredRef.current) return;
    if (urlAppIsValidForOrg === null) return;       // wait for apps
    if (urlAppIsValidForOrg) {
      // URL's app belongs to this org — respect it as the user's
      // explicit pick. Mark fired so the persistence effect kicks in
      // on subsequent picks.
      defaultAppFiredRef.current = true;
      return;
    }
    if (!defaultAppQuery.data) return;              // wait for default
    defaultAppFiredRef.current = true;
    const { app_id, kind } = defaultAppQuery.data;
    if (kind === "no_apps") {
      // Org has zero apps — picker will be hidden by render. Clear
      // any stale `?app=` from the URL.
      setFilters({ appId: "all" });
      return;
    }
    if (app_id !== null) {
      setFilters({ appId: app_id });
    } else {
      // kind = 'all_apps' or user-sticky-is-all → URL should
      // explicitly reflect "all" so refresh doesn't trigger the
      // auto-resolve loop next time.
      setFilters({ appId: "all" });
    }
  }, [
    urlAppIsValidForOrg,
    defaultAppQuery.data,
    setFilters,
  ]);

  // Persist the user's pick to the server whenever it changes — but
  // only after the initial default was applied, otherwise we'd write
  // the just-resolved default back over itself (harmless but noisy).
  useEffect(() => {
    // Only persist explicit picks (see userPickedAppRef) — not the
    // auto-applied default, which would freeze the top-7d-spender rule.
    if (!userPickedAppRef.current) return;
    if (filters.appId === "all") {
      setDefaultApp.mutate({ app_id: null, is_all: true });
    } else {
      setDefaultApp.mutate({ app_id: filters.appId, is_all: false });
    }
    // setDefaultApp identity changes per render — pin to filters.appId
    // so we only fire on actual pick changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.appId]);

  // Search + status filter are sticky across navigation (into a campaign
  // and back) — seeded from localStorage, persisted on change — so they
  // stay until the user changes them, not reset on every remount.
  const [status, setStatus] = useState<"all" | "active" | "paused">(() => {
    const v = localStorage.getItem(STATUS_LS_KEY);
    return v === "active" || v === "paused" || v === "all" ? v : "all";
  });
  const [search, setSearch] = useState(
    () => localStorage.getItem(SEARCH_LS_KEY) ?? "",
  );
  useEffect(() => {
    try {
      localStorage.setItem(STATUS_LS_KEY, status);
    } catch {
      /* ignore quota / private-mode */
    }
  }, [status]);
  useEffect(() => {
    try {
      localStorage.setItem(SEARCH_LS_KEY, search);
    } catch {
      /* ignore quota / private-mode */
    }
  }, [search]);
  // Debounce search → backend so a fast typist doesn't fire a fresh
  // query on every keystroke. 350ms is the sweet spot — fast enough
  // that UI feels live, slow enough to coalesce normal typing into
  // one query per word. Same hook is now used by the four search
  // inputs in CampaignDetailPage so behavior is consistent.
  const searchDebounced = useDebouncedValue(search, 350);
  const [limit, setLimit] = useState(30);
  const [offset, setOffset] = useState(0);
  const [visibleCols, _setVisibleColsState] = useState<ColumnKey[]>(() =>
    loadVisibleColumns("campaigns")
  );
  // Wrapped setter: any change to visible columns (preset apply,
  // drag-reorder, edit-columns modal, default-preset auto-apply) is
  // persisted to localStorage immediately. Without this, applying a
  // preset only changes React state — the next page reload reads the
  // OLD localStorage value, so the user had to re-apply the preset
  // each time. The wrapped setter accepts both bare values and
  // updater functions for parity with the raw useState setter.
  const setVisibleCols = (
    next: ColumnKey[] | ((prev: ColumnKey[]) => ColumnKey[]),
  ) => {
    _setVisibleColsState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      saveVisibleColumns("campaigns", resolved);
      return resolved;
    });
  };
  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    loadColumnWidths("campaigns"),
  );
  const onWidthChange = (key: string, px: number) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: px };
      saveColumnWidths("campaigns", next);
      return next;
    });
  };
  // Cohort source of the selected app drives which conversion-event
  // columns are offered: 'adjust' apps show the 10 Adjust events, all
  // others (and the "all apps" view) show the Adapty events. Declared
  // BEFORE the default-preset auto-apply effect so the apply can gate
  // on `cohortReady` — without that gate, the apply runs while
  // cohortSource is undefined (cohortCfg still loading),
  // allowedColumnsForTab takes the "drop adj_*" default branch, and a
  // user's Adjust-app preset has its adj_* cols stripped wrongly.
  const cohortCfg = useAppCohortConfig(
    typeof filters.appId === "number" ? filters.appId : null,
  );
  const cohortSource = cohortCfg.data?.source;
  // cohortReady gates the default-preset auto-apply until cohortSource
  // is DEFINITIVELY known. The naive `!cohortCfg.isLoading` (used
  // 2026-05-30) fires true too early in two windows, both of which
  // strip an Adjust app's adj_* columns from the applied preset and
  // then burn appliedDefaultRef so the correct preset never re-applies
  // — the "columns reset every time" bug:
  //   - On mount appId is "all" before the default-app resolver runs,
  //     so the cohort query is disabled (isLoading=false → looks ready)
  //     even though a real app (with a real cohortSource) is about to
  //     be selected.
  //   - For a specific app, the query is enabled but hasn't fetched yet.
  // So: for a specific app, wait for its cohort query to have fetched
  // at least once (isFetched). For the "all apps" view, only treat it
  // as ready once the default-app resolver has fetched AND its verdict
  // is genuinely "no specific app" (app_id === null) — otherwise we're
  // in the transient "all" before the resolver flips appId to a real
  // app, and applying now would use the wrong (undefined) cohortSource.
  const cohortReady =
    typeof filters.appId === "number"
      ? cohortCfg.isFetched
      : defaultAppQuery.isFetched &&
        (defaultAppQuery.data?.app_id ?? null) === null;

  // Auto-apply the user's saved default preset (if any) on first
  // mount. localStorage seeded `visibleCols` + `colWidths` synchronously
  // above so the page paints something immediately; once /api/column-
  // presets resolves we overwrite with the default. `appliedDefaultRef`
  // ensures this only fires once — subsequent invalidations (after the
  // user toggles default in the dropdown) shouldn't re-stomp manual
  // adjustments.
  const presetsQuery = useColumnPresets("campaigns");
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (!presetsQuery.data) return;
    if (!cohortReady) return;
    appliedDefaultRef.current = true;
    const dft = presetsQuery.data.find((p) => p.is_default);
    if (!dft) return;
    setVisibleCols(
      resolvePresetColumns(
        "campaigns",
        dft.columns,
        allowedColumnsForTab("campaigns", cohortSource),
      ),
    );
    const w = dft.widths ?? {};
    setColWidths(w);
    saveColumnWidths("campaigns", w);
  }, [presetsQuery.data, cohortReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [sort, setSort] = useState<{ by: string | null; dir: "asc" | "desc" }>(
    { by: null, dir: "desc" }
  );

  // Reset to page 1 when search / status / sort changes — otherwise
  // the user can be left on a high page that's empty under the new
  // filter (e.g. typed "abc", was on offset=60, total filtered = 12).
  useEffect(() => {
    setOffset(0);
  }, [search, status, sort.by, sort.dir, adsFilter.apiParam]);

  const statsFilters = useMemo(() => adsFiltersToStatsFilters(filters), [filters]);
  // Columns actually rendered: filter the saved visible set by the
  // app's cohort source so Adjust apps never render Adapty-sourced
  // columns (events / aggregate Revenue / cohort D-cols) even if a
  // stale localStorage set still lists them.
  const shownCols = useMemo(() => {
    const allowed = new Set(allowedColumnsForTab("campaigns", cohortSource));
    return visibleCols.filter((k) => allowed.has(k));
  }, [visibleCols, cohortSource]);
  const refreshAppsFromAsa = useRefreshAppsFromAsa();
  // §37 — explicit-status copy under the "Synchronize Now" footer in
  // the app picker. The existing header refresh button shares the
  // same mutation but routes its outcome through the page-level
  // toast instead of inline picker copy.
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const campaignsQuery = useCampaignsTable(
    statsFilters,
    { limit, offset },
    status,
    sort,
    searchDebounced,
    adsFilter.apiParam,
    adsFilter.splitOn,
  );
  const labelsQuery = useLabels();
  const updateCampaign = useUpdateCampaign();

  // Apply-CPP modal — wired to the live ASA endpoints. The CPP list
  // is fetched per-app (the "all apps" filter state has no canonical
  // adamId so the picker is empty in that case; user has to narrow).
  const [applyCppOpen, setApplyCppOpen] = useState(false);
  const [editBidBudgetOpen, setEditBidBudgetOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [rulesManagerOpen, setRulesManagerOpen] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState<
    "single" | "multi" | null
  >(null);
  const [toast, setToast] = useState<
    {
      msg: string;
      variant: "success" | "error";
      action?: { label: string; onClick: () => void };
    } | null
  >(null);
  // Confirmation gate for bulk Pause/Enable when ≥2 campaigns are
  // selected. The fanout is 32 PATCHes in 1.2s with no UI feedback,
  // so the silent-mass-mutation class of mistakes was real (incident
  // 2026-05-04: 32 prod CPA campaigns paused without anyone noticing
  // for 24h). One-campaign actions stay one-click for muscle memory.
  const [statusConfirm, setStatusConfirm] = useState<
    { target: "ENABLED" | "PAUSED"; ids: number[] } | null
  >(null);
  // Resolve the ASA adamId for the CPP picker. Two paths, in order:
  //   1. The user has narrowed to a single app via the App filter →
  //      use that app's adamId.
  //   2. Filter is "All apps" but the user has campaigns selected →
  //      pull the adamId from the first selected campaign's app_id.
  //      When all selected campaigns share one app this is unambiguous;
  //      when they span multiple apps we still pick the first (and the
  //      backend's bulk-apply naturally hits ad_groups under campaigns
  //      from any app, so this is fine in practice).
  // Otherwise the hook stays disabled and the picker is empty.
  const pickedApp =
    filters.appId === "all"
      ? null
      : (appsQuery.data ?? []).find((a) => a.id === filters.appId) ?? null;
  const firstSelectedCampaign = (campaignsQuery.data?.rows ?? []).find((r) =>
    selected.has(r.campaign_id)
  );
  const fallbackAppId = firstSelectedCampaign?.app_id ?? null;
  const fallbackApp =
    fallbackAppId !== null
      ? (appsQuery.data ?? []).find((a) => a.id === fallbackAppId) ?? null
      : null;
  const asaAppId =
    pickedApp?.asa_app_id ?? fallbackApp?.asa_app_id ?? null;
  const cppQuery = useCustomProductPages(asaAppId);
  // Use product_page_id as the modal's unique key — creative_id is
  // 0 both for the synthesized DEFAULT row AND for any custom CPP
  // that doesn't have an ASA Creative wrapper yet (freshly created in
  // App Store Connect). Using String(creative_id) as the key would
  // collide those rows together, making "select CPP" highlight
  // multiple rows and the preview pick whichever comes first.
  // product_page_id is the UUID for custom CPPs and null for the
  // default; "default" sentinel disambiguates.
  const cppOptions: CppOption[] = (cppQuery.data?.rows ?? []).map((c) => ({
    id: c.product_page_id ?? "default",
    name: c.name,
    screenshots: c.screenshots ?? [],
    externalUrl: c.external_url ?? undefined,
  }));
  const bulkApplyCpp = useBulkApplyCpp();
  const bulkEditBidBudget = useBulkEditBidBudget();
  const bulkDuplicateCampaign = useBulkDuplicateCampaign();
  const flashToast = (
    msg: string,
    variant: "success" | "error" = "success",
    action?: { label: string; onClick: () => void },
  ) => {
    setToast({ msg, variant, action });
    window.setTimeout(() => setToast(null), action ? 8000 : 5000);
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectionReadOnly = selectedIds.some((id) => readOnlyCampaigns?.has(id));

  // Whether ≥1 selected campaign is Max-Conversions — drives the Rules
  // Manager's hiding of max-conv-only rules.
  const hasMaxConvSelected = useMemo(
    () =>
      (campaignsQuery.data?.rows ?? []).some(
        (r) =>
          selected.has(r.campaign_id) &&
          r.bidding_strategy === "MAX_CONVERSIONS",
      ),
    [campaignsQuery.data, selected],
  );

  /** Приложение выбранных кампаний — им фильтруется список правил.
   *
   *  ★ Берётся из САМИХ строк, а не из фильтра приложения: на виде «все
   *  приложения» фильтр не назван, а выделение всё равно однородно почти
   *  всегда. Разнородное выделение даёт null — тогда не фильтруем, и
   *  заслон остаётся на сервере. */
  const selectedAppId = useMemo(() => {
    const apps = new Set(
      (campaignsQuery.data?.rows ?? [])
        .filter((r) => selected.has(r.campaign_id))
        .map((r) => r.app_id)
        .filter((a): a is number => typeof a === "number"),
    );
    return apps.size === 1 ? [...apps][0] : null;
  }, [campaignsQuery.data, selected]);

  // Push the status to ASA via N parallel PATCHes (one per selected
  // campaign). The 202s queue into `pending_writes`; drainer pushes.
  // Returns the per-id pending_write_id so the caller can wait for
  // ASA confirmation per id (allSettled — a 4xx from our API on one
  // shouldn't kill the rest).
  const fanoutStatusUpdate = async (
    target: "ENABLED" | "PAUSED",
    ids: number[],
  ): Promise<{
    accepted: { campaign_id: number; pending_write_id: number }[];
    rejected: { campaign_id: number; error: string }[];
  }> => {
    const settled = await Promise.allSettled(
      ids.map(async (campaign_id) => {
        const handle = await updateCampaign.mutateAsync({
          campaign_id,
          status: target,
        });
        return { campaign_id, pending_write_id: handle.pending_write_id };
      }),
    );
    const accepted: { campaign_id: number; pending_write_id: number }[] = [];
    const rejected: { campaign_id: number; error: string }[] = [];
    settled.forEach((r, i) => {
      if (r.status === "fulfilled") accepted.push(r.value);
      else rejected.push({
        campaign_id: ids[i]!,
        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
      });
    });
    return { accepted, rejected };
  };

  const runBulkStatus = async (target: "ENABLED" | "PAUSED", ids: number[]) => {
    if (ids.length === 0) return;
    // Clear the row selection the moment the action is dispatched (the
    // ≥2 path has already confirmed; the single-item path is implicitly
    // confirmed by the click). `ids` is captured in this closure so the
    // in-flight fanout + ASA-confirm poll still complete. Without this
    // the selection lingered and the NEXT selection accumulated on top
    // of it, so the confirm modal showed old-count + new-count. The
    // Undo toast re-runs with its own captured `ids`, unaffected.
    setSelected(new Set());
    // Sentry breadcrumb + (>=10) loud event so any subsequent error
    // carries the action in its trail, and large-scale fanouts raise
    // a dashboard message even when nothing fails.
    reportBulkAction({
      resource: "campaign",
      action: target === "PAUSED" ? "pause" : "enable",
      count: ids.length,
      details: { ids: ids.slice(0, 100) },
    });

    const verbPast = target === "PAUSED" ? "paused" : "enabled";
    const reverse: "ENABLED" | "PAUSED" =
      target === "PAUSED" ? "ENABLED" : "PAUSED";

    // Phase 1: hand the request to our API (202 = queued for drainer).
    // No toast yet — we want notifications only when ASA replies with
    // the expected state.
    const { accepted, rejected } = await fanoutStatusUpdate(target, ids);

    if (rejected.length === ids.length) {
      flashToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} campaign${ids.length === 1 ? "" : "s"}: ${rejected[0]!.error}`,
        "error",
      );
      return;
    }

    // Phase 2: wait for the drainer to push to ASA + ASA to reply.
    // The pending-writes tracker polls /api/pending-writes/state every
    // 1.5s and resolves each id when its status flips to synced/error.
    // We only show the success toast once everything ASA-confirmed.
    const tracked = accepted
      .filter((a) => a.pending_write_id > 0)
      .map((a) => ({ id: a.pending_write_id, resourceType: "campaign" as const }));
    const results = await awaitPendingWrites(tracked);

    let confirmed = 0;
    let failed = rejected.length;
    let timedOut = 0;
    let lastError: string | null = null;
    for (const r of results.values()) {
      if (r.status === "synced") confirmed++;
      else if (r.status === "error") {
        failed++;
        lastError = r.errorMessage ?? lastError;
      }
      else timedOut++;
    }

    if (confirmed === ids.length) {
      flashToast(
        `${confirmed} campaign${confirmed === 1 ? "" : "s"} ${verbPast} on ASA`,
        "success",
        {
          label: "Undo",
          onClick: () => {
            // Undo: fire the reverse status update on the same set.
            // Fail-soft — toast hide doesn't block the network call.
            void runBulkStatus(reverse, ids);
            setToast(null);
          },
        },
      );
    } else if (confirmed > 0) {
      const detail =
        failed > 0
          ? `${failed} failed${lastError ? ` (${lastError.slice(0, 80)})` : ""}`
          : `${timedOut} still syncing`;
      flashToast(
        `${confirmed} ${verbPast}, ${detail}`,
        "error",
      );
    } else if (timedOut === ids.length) {
      flashToast(
        `ASA hasn't confirmed yet — try again in a moment.`,
        "error",
      );
    } else {
      flashToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} campaign${ids.length === 1 ? "" : "s"}${lastError ? `: ${lastError.slice(0, 80)}` : ""}`,
        "error",
      );
    }
  };

  const bulkSetStatus = (target: "ENABLED" | "PAUSED") => {
    if (selectedIds.length === 0) return;
    // ≥2 selections gate behind a confirmation modal — this is the
    // primary defense against the 1-second-32-pauses class of
    // accident. Single-campaign actions stay one-click for speed.
    if (selectedIds.length >= 2) {
      setStatusConfirm({ target, ids: selectedIds });
      return;
    }
    void runBulkStatus(target, selectedIds);
  };

  const appItems = useMemo<EntityItem[]>(
    () =>
      (appsQuery.data ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        // Placeholder bundle ids (`asa-<adamId>`) are internal scaffolding,
        // not something a human should see. Drop them from the picker —
        // show the real bundle id if we have one, otherwise nothing.
        sub: isRealBundleId(a.bundle_id) ? a.bundle_id! : "",
        tint: tintFor(a.id),
        initials: initialsFor(a.name),
        iconUrl: a.icon_url,
      })),
    [appsQuery.data]
  );

  // Enrich rows with the real App Store artwork URL by joining
  // app_id against /api/apps. Stays optional — when the apps query
  // is still loading or the campaign has no app_id binding (rare),
  // the row falls back to the 2-letter badge in StatsTable.
  const appIconByID = useMemo(() => {
    const m = new Map<number, string | null>();
    for (const a of appsQuery.data ?? []) m.set(a.id, a.icon_url ?? null);
    return m;
  }, [appsQuery.data]);

  // Search is now applied server-side (passed to useCampaignsTable);
  // here we only enrich with app_icon_url. No client-side filter —
  // that's what fragmented "4 / 2 / 3 rows per page" output.
  const displayed = useMemo<CampaignStatsRow[]>(() => {
    return (campaignsQuery.data?.rows ?? []).map((r) => ({
      ...r,
      app_icon_url:
        r.app_id != null ? appIconByID.get(r.app_id) ?? null : null,
    }));
  }, [campaignsQuery.data, appIconByID]);

  const total = campaignsQuery.data?.total ?? 0;
  const activeCount = campaignsQuery.data?.active_count ?? 0;

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = (ids: number[], selectAll: boolean) => {
    const next = new Set(selected);
    for (const id of ids) {
      if (selectAll) next.add(id);
      else next.delete(id);
    }
    setSelected(next);
  };

  // Right-aligned filter strip + bell, mirroring Overview's header.
  // Reused class `.ov-filter-row` enforces a 52px row so App / Filter
  // / Date / Bell all line up on identical top + bottom baselines.
  const rightSlot = (
    <div
      className="row ov-filter-row"
      style={{
        marginLeft: "auto",
        alignItems: "stretch",
        height: 52,
        gap: 10,
        flex: "0 0 auto",
        flexWrap: "nowrap",
        justifyContent: "flex-end",
      }}
    >
      <SingleEntityPicker<"all" | number>
        items={appItems}
        value={filters.appId}
        onChange={(v) => {
          userPickedAppRef.current = true;
          setFilters({ appId: v });
        }}
        label="App"
        width={260}
        allOption={{
          id: "all",
          name:
            appsQuery.isLoading || appItems.length === 0
              ? "All apps"
              : `All ${appItems.length} apps`,
          sub: appsQuery.isLoading
            ? "Loading…"
            : appItems.length === 1
              ? "1 app"
              : `${appItems.length} apps`,
        }}
        onRefresh={async () => {
          try {
            const res = await refreshAppsFromAsa.mutateAsync();
            const parts: string[] = [];
            if (res.discovered > 0) parts.push(`${res.discovered} new`);
            if (res.refreshed > 0) parts.push(`${res.refreshed} updated`);
            setToast({
              msg: parts.length
                ? `${parts.join(" · ")} app${
                    res.discovered + res.refreshed === 1 ? "" : "s"
                  } from ASA`
                : `Already up to date (${res.total_local} apps)`,
              variant: "success",
            });
            window.setTimeout(() => setToast(null), 4000);
          } catch (e) {
            const msg =
              (e as { response?: { data?: { error?: { message?: string } } } })
                ?.response?.data?.error?.message ?? "Refresh failed";
            setToast({ msg, variant: "error" });
            window.setTimeout(() => setToast(null), 5000);
          }
        }}
        isRefreshing={refreshAppsFromAsa.isPending}
        syncNow={{
          onClick: () => {
            setSyncStatusMsg(null);
            refreshAppsFromAsa.mutate(undefined, {
              onSuccess: (data) => {
                const parts: string[] = [];
                if (data.discovered > 0) parts.push(`${data.discovered} new`);
                if (data.refreshed > 0) parts.push(`${data.refreshed} updated`);
                setSyncStatusMsg(
                  parts.length
                    ? `${parts.join(" · ")} · ${data.total_local} apps`
                    : "Already up to date · " + data.total_local + " apps",
                );
              },
              onError: () => {
                setSyncStatusMsg(
                  "Sync failed — check Settings → ASA integration",
                );
              },
            });
          },
          loading: refreshAppsFromAsa.isPending,
          status: syncStatusMsg,
        }}
      />
      <FilterMenuButton />
      <DateRangePicker
        preset={filters.preset}
        range={filters.range}
        mode={filters.mode}
        onChange={({ preset, range, mode }) =>
          setFilters({ preset, range, mode })
        }
      />
      <UserAlertsBell />
    </div>
  );

  return (
    <LabelsProvider labels={labelsQuery.data ?? []}>
      <Topbar rightSlot={rightSlot} />
      {/* Ход Apply CPP. Тот же бар, что у Campaign Studio, и та же
          позиция — между Topbar и содержимым. Ничего не рисует, пока
          нет активного задания. Применение CPP синхронно и занимает по
          секунде на группу; без бара две минуты выглядят как «не
          сработало» (замер 24.08: 117 с, HTTP 200, и человек всё равно
          решил, что применение не прошло). */}
      <CampaignStudioProgressBar />

      <div className="content ads-density">
        {/* Breadcrumbs row — kept directly under the topbar so the
            scope ("Account / Campaigns") stays visually anchored to
            the page even though the filter strip moved up.
            Sticky-stacked: pins under the topbar so it never scrolls
            out of view, matching the topbar/toolbar/thead trio. */}
        <div className="ads-sticky-crumbs">
          <Breadcrumbs items={[{ label: "Account" }, { label: "Campaigns" }]} />
        </div>

        {chartOpen && (
          <div className="ads-chart-slot">
            <EntityChartPanel
              level="campaign"
              appId={typeof filters.appId === "number" ? filters.appId : null}
              entityId={selectedIds.length === 1 ? selectedIds[0]! : null}
              entityName={
                selectedIds.length === 1
                  ? ((campaignsQuery.data?.rows ?? []).find(
                      (r) => r.campaign_id === selectedIds[0],
                    )?.campaign_name ?? null)
                  : null
              }
              dateFrom={ymd(filters.range.from)}
              dateTo={ymd(filters.range.to)}
              onClose={() => setChartOpen(false)}
            />
          </div>
        )}

        {/* Toolbar — single row. Left side: search + status + bulk
            actions. Right side: active badge + columns + new.
            `flexWrap: nowrap` was the whole problem before — the
            right-side buttons kept wrapping below the left-side
            controls because the container defaulted to wrapping.
            Sticky-stacked below the breadcrumbs — search box +
            Columns dropdown stay reachable while scrolling rows.
            The sticky wrapper is OUTSIDE `.card` so its
            padding-bottom creates a gap between the card and the
            table-wrap below, not inside the card. */}
        <div className="ads-sticky-toolbar">
        <div className="card">
          <div
            ref={toolbarRef}
            className="table-toolbar"
            style={{ flexWrap: "nowrap", gap: 10, alignItems: "center" }}
          >
            <div className="input-group" style={{ width: 280, flex: "none" }}>
              {I.search}
              <input
                className="input"
                placeholder="Search campaigns"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <SegRange<"all" | "active" | "paused">
              value={status}
              onChange={setStatus}
              options={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
              ]}
            />
            {selectionReadOnly && (
              <span
                className="muted"
                style={{ fontSize: 12 }}
                title="Read-only access — reconnect with Read & Write to enable changes."
              >
                Read-only access — reconnect with Read &amp; Write to enable changes.
              </span>
            )}
            <BulkActions
              selectedIds={selectedIds}
              canEdit={canEdit && !selectionReadOnly}
              onEnable={() => void bulkSetStatus("ENABLED")}
              onPause={() => void bulkSetStatus("PAUSED")}
              busy={updateCampaign.isPending}
              entityType="campaign"
              onToggleChart={() => setChartOpen((v) => !v)}
              chartOpen={chartOpen}
              showActions
              showRules
              onRulesManager={() => setRulesManagerOpen(true)}
              onActionPick={(v) => {
                if (v === "apply_cpp") setApplyCppOpen(true);
                else if (v === "edit_bid_budget") setEditBidBudgetOpen(true);
                else if (v === "rename") setRenameOpen(true);
                else if (v === "duplicate") {
                  // 1 → single modal (lets user edit the new name);
                  // 2+ → multi modal (auto-suffix per source).
                  setDuplicateMode(
                    selectedIds.length === 1 ? "single" : "multi",
                  );
                } else window.alert(`${v} — wired in v1.1`);
              }}
            />
            {adsFilter.showChips && !chipsWrapped && (
              <div ref={chipsRef} style={{ flex: "none" }}>
                <FilterChips
                  applied={adsFilter.applied}
                  describe={adsFilter.describe}
                  onOpen={adsFilter.openModal}
                  onRemove={adsFilter.removeType}
                  split={adsFilter.splitOn ? { onRemove: () => adsFilter.setSplit(false) } : undefined}
                />
              </div>
            )}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 8,
                alignItems: "center",
                flex: "none",
              }}
            >
              <span
                className="badge live-badge"
                title={`${fmtInt(total)} campaigns match current filters`}
              >
                <span className="dot-live" />
                {fmtInt(activeCount)} Active
              </span>
              <ColumnsButton
                tab="campaigns"
                visible={visibleCols}
                onChange={setVisibleCols}
                allowedKeys={allowedColumnsForTab("campaigns", cohortSource)}
                currentWidths={colWidths}
                onApplyWidths={(w) => {
                  setColWidths(w);
                  saveColumnWidths("campaigns", w);
                }}
              />
              {canEdit && (
                <Btn
                  variant="primary"
                  icon={I.plus}
                  disabled
                  title="New campaign — v1.1"
                >
                  New campaign
                </Btn>
              )}
            </div>
          </div>
          {adsFilter.showChips && chipsWrapped && (
            <div ref={chipsRef} className="af-chips-row">
              <FilterChips
                applied={adsFilter.applied}
                describe={adsFilter.describe}
                onOpen={adsFilter.openModal}
                onRemove={adsFilter.removeType}
                split={adsFilter.splitOn ? { onRemove: () => adsFilter.setSplit(false) } : undefined}
              />
            </div>
          )}
        </div>
        </div>

        {campaignsQuery.data && <MappingGateBanner gate={campaignsQuery.data.gate} />}

        {campaignsQuery.isLoading && (
          <div className="card card-pad muted" style={{ textAlign: "center" }}>
            Loading campaigns…
          </div>
        )}
        {campaignsQuery.error && (
          <div className="card card-pad" style={{ textAlign: "center" }}>
            <div className="serif" style={{ fontSize: 20 }}>
              Couldn&apos;t load campaigns
            </div>
            <div style={{ marginTop: 14 }}>
              <Btn variant="primary" onClick={() => void campaignsQuery.refetch()}>
                Try again
              </Btn>
            </div>
          </div>
        )}

        {campaignsQuery.data && (
          <div className="table-wrap">
            <StatsTable<"campaign">
              busy={campaignsQuery.isPlaceholderData}
              deviceSplit={adsFilter.splitOn}
              grain="campaign"
              rows={displayed}
              emptyContent={
                adsFilter.isActive ? (
                  <FilterEmptyState
                    noun="campaigns"
                    applied={adsFilter.applied}
                    onOpen={adsFilter.openModal}
                    onReset={adsFilter.reset}
                  />
                ) : undefined
              }
              visibleCols={shownCols}
              widths={colWidths}
              onWidthChange={onWidthChange}
              sort={sort}
              onSortChange={setSort}
              selection={{
                ids: selected,
                onToggle: toggle,
                onToggleAll: toggleAll,
              }}
              canEditMoney={canEdit}
              readOnlyRowIds={readOnlyCampaigns}
              onCommitDailyBudget={(campaign_id, daily_budget) =>
                void runWithAsaConfirmToast({
                  mutation: updateCampaign.mutateAsync({
                    campaign_id,
                    daily_budget,
                  }),
                  resourceType: "campaign",
                  fieldLabel: "Daily budget",
                  flash: flashToast,
                })
              }
              onCommitTargetCpa={(campaign_id, target_cpa) =>
                void runWithAsaConfirmToast({
                  mutation: updateCampaign.mutateAsync({
                    campaign_id,
                    target_cpa,
                  }),
                  resourceType: "campaign",
                  fieldLabel: "Target CPA",
                  flash: flashToast,
                })
              }
              serverTotals={campaignsQuery.data.totals}
              unfilteredTotals={campaignsQuery.data.unfiltered_totals}
              deviceTotals={campaignsQuery.data.device_totals}
              totalsLabel={(() => {
                // "Total by <App Name>" when a single app is picked,
                // "Total by <Account Name>" otherwise. Falls back to
                // a generic "Total by Account" while the apps query
                // hasn't resolved yet (rare, only on first paint).
                //
                // Long org names wrap the tfoot row to two lines —
                // which then breaks the sticky-stack alignment with
                // the filtered row above. Cap the suffix to ~24
                // chars so "Total by " (9 chars) + name fits inside
                // the identity column on a 13" laptop without
                // wrapping. Full name is still surfaced via title.
                const truncate = (s: string, n: number) =>
                  s.length > n ? s.slice(0, n - 1) + "…" : s;
                if (filters.appId !== "all") {
                  const app = (appsQuery.data ?? []).find(
                    (a) => a.id === filters.appId
                  );
                  return `Total by ${truncate(app?.name ?? "App", 24)}`;
                }
                return `Total by ${truncate(user?.org_name ?? "Account", 24)}`;
              })()}
              totalsCount={campaignsQuery.data.unfiltered_count}
              totalsCountWord="campaign"
              filteredTotalsLabel={
                // Show the second "filtered" row only when the user
                // has actually narrowed the visible set via status
                // or name search. Without a filter the unfiltered
                // and filtered numbers are identical and a duplicate
                // row is just noise.
                // Use the debounced value so the row's appearance
                // matches what the backend has actually computed —
                // not the in-flight typing state.
                // «Split by devices» shows it always: the Total for
                // iPhone / iPad lines hang under Total filtered, and
                // Total by <App> is never split. Unnarrowed, its numbers
                // equal Total by <App> — the same scope.
                status !== "all" ||
                searchDebounced.trim() !== "" ||
                adsFilter.isActive ||
                adsFilter.splitOn
                  ? "Total filtered campaigns"
                  : undefined
              }
              filteredTotalsCount={campaignsQuery.data.total}
              onRowClick={(row) =>
                navigate(
                  `/ads/campaigns/${row.campaign_id}?${rowLinkParams(filters).toString()}`
                )
              }
            />
            <Pager
              total={total}
              limit={limit}
              offset={offset}
              onLimitChange={setLimit}
              onOffsetChange={setOffset}
            />
          </div>
        )}
      </div>

      <EditBidBudgetModal
        open={editBidBudgetOpen}
        onClose={() => setEditBidBudgetOpen(false)}
        selectedCampaigns={(campaignsQuery.data?.rows ?? [])
          .filter((c) => selectedIds.includes(c.campaign_id))
          .map((c) => ({
            campaign_id: c.campaign_id,
            bidding_strategy: c.bidding_strategy ?? null,
          }))}
        filters={filters}
        onApply={(request) => {
          // Sentry breadcrumb on the way in — captures intent even
          // if the mutation later fails. count is # campaigns picked
          // (the actual fanout multiplies by ad_groups/keywords inside
          // each, but the breadcrumb's `details` carries the request).
          reportBulkAction({
            resource:
              request.level === "campaign"
                ? "campaign"
                : request.level === "ad_group"
                ? "ad_group"
                : "keyword",
            action: `edit_${request.fields.join("+")}`,
            count: request.campaign_ids.length,
            details: {
              fields: request.fields,
              dir: request.action,
              value: request.value != null ? String(request.value) : null,
              unit: request.value_unit,
            },
          });
          // Fire-and-forget. Mutation runs in the background; user
          // can close the modal or queue more edits while ASA fans
          // out the PUTs. Result toast appears regardless of whether
          // the modal is still open.
          bulkEditBidBudget.mutate(request, {
            onSuccess: (result) => {
              let kind: "success" | "error" = "success";
              let msg: string;
              if (result.success_count > 0 && result.failure_count === 0) {
                msg = `${result.success_count} ${result.field_label} changed`;
              } else if (
                result.success_count > 0 &&
                result.failure_count > 0
              ) {
                kind = "error";
                msg = `${result.success_count} ${result.field_label} changed, ${result.failure_count} failed. Try again`;
              } else {
                kind = "error";
                msg = `${result.failure_count} ${result.field_label} didn't change. Try again`;
              }
              flashToast(msg, kind);
            },
            onError: (e) => {
              const msg = `Bulk edit failed: ${e instanceof Error ? e.message : String(e)}`;
              flashToast(msg, "error");
            },
          });
        }}
      />

      <RenameCampaignModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        campaign={(() => {
          // Modal is rendered only with exactly one selected campaign
          // (BulkActions hides the menu item otherwise). Pluck name
          // from the visible rows so we don't need a separate fetch.
          if (selectedIds.length !== 1) return null;
          const id = selectedIds[0];
          const row = (campaignsQuery.data?.rows ?? []).find(
            (c) => c.campaign_id === id,
          );
          if (!row) return null;
          return {
            campaign_id: row.campaign_id,
            name: row.campaign_name ?? "",
          };
        })()}
        onApply={async (campaign_id, update) => {
          // Foreground: await only the 202 enqueue. If our backend
          // refuses (e.g. 422 campaign_name_already_in_use from the
          // collision check), the throw is caught by the modal and
          // surfaced inline so the user can correct.
          const handle = await updateCampaign.mutateAsync({
            campaign_id,
            ...update,
          });
          // Background: poll pending_writes until ASA confirms; toast
          // the final outcome. Don't await here — modal closes the
          // moment this onApply resolves so the user can keep going.
          void runWithAsaConfirmToast({
            mutation: Promise.resolve(handle),
            resourceType: "campaign",
            fieldLabel: "Name",
            flash: flashToast,
          });
        }}
      />

      <DuplicateCampaignModal
        open={duplicateMode !== null}
        onClose={() => setDuplicateMode(null)}
        source={(() => {
          if (duplicateMode !== "single") return null;
          if (selectedIds.length !== 1) return null;
          const id = selectedIds[0];
          const row = (campaignsQuery.data?.rows ?? []).find(
            (c) => c.campaign_id === id,
          );
          if (!row) return null;
          return {
            campaign_id: row.campaign_id,
            name: row.campaign_name ?? "",
          };
        })()}
        multiCampaignIds={
          duplicateMode === "multi" ? Array.from(selectedIds) : []
        }
        onApply={async ({ campaign_ids, new_name_override, toggles }) => {
          const overrides =
            new_name_override && campaign_ids.length === 1
              ? { [String(campaign_ids[0])]: new_name_override }
              : null;
          // Phase 1 (foreground): enqueue and AWAIT only the 202.
          // After this, modal closes — the user wants to keep
          // working and not stare at a spinning button for 5-30s
          // while ASA cascades behind the scenes.
          const result = await bulkDuplicateCampaign.mutateAsync({
            campaign_ids,
            new_name_overrides: overrides,
            enable: toggles.enable,
            include_keywords: toggles.include_keywords,
            include_negatives: toggles.include_negatives,
          });

          // Edge: every requested id was rejected at validation
          // (name collision, missing campaign, etc). Show the toast
          // synchronously; modal closes after this returns.
          if (result.pending_write_ids.length === 0) {
            flashToast(
              `Couldn't queue: ${result.rejected[0]?.error ?? "rejected"}`,
              "error",
            );
            return;
          }

          // Phase 2 (background): poll pending_writes per source and
          // toast the final outcome when the drainer reports back.
          // Fire-and-forget — `void` the promise so this onApply
          // resolves immediately and the modal closes. The
          // pending-writes-tracker (mounted in App.tsx) keeps
          // running across page navigations, so the toast still
          // surfaces even if the user has moved elsewhere by then.
          const tracked = result.pending_write_ids.map((id) => ({
            id,
            resourceType: "campaign" as const,
          }));
          void awaitPendingWrites(tracked).then((outcomes) => {
            let synced = 0;
            let errors = 0;
            let timeouts = 0;
            let firstError: string | null = null;
            for (const r of outcomes.values()) {
              if (r.status === "synced") synced += 1;
              else if (r.status === "error") {
                errors += 1;
                if (!firstError) firstError = r.errorMessage ?? "ASA rejected";
              } else timeouts += 1;
            }
            // Aggregate the immediate-rejected (Phase 1) into the
            // failure tally so the user sees one summary toast.
            const totalErrors = errors + result.rejected.length;
            if (synced > 0 && totalErrors === 0 && timeouts === 0) {
              flashToast(
                `${synced} ${synced === 1 ? "campaign" : "campaigns"} duplicated on ASA`,
                "success",
              );
            } else if (synced > 0 && (totalErrors > 0 || timeouts > 0)) {
              const detail = firstError
                ? ` (${firstError.slice(0, 80)})`
                : "";
              flashToast(
                `${synced} duplicated, ${totalErrors + timeouts} failed${detail}`,
                "error",
              );
            } else if (timeouts > 0 && totalErrors === 0) {
              flashToast(
                `Duplicate is taking longer than usual — refresh in a moment`,
                "error",
              );
            } else {
              const detail =
                firstError
                ?? result.rejected[0]?.error
                ?? "ASA rejected the duplicate";
              flashToast(
                `Duplicate failed: ${detail.slice(0, 120)}`,
                "error",
              );
            }
          });
        }}
      />

      <ApplyCppModal
        open={applyCppOpen}
        onClose={() => setApplyCppOpen(false)}
        selectedCount={selectedIds.length}
        entityKind="campaign"
        cppOptions={cppOptions}
        onRefresh={cppQuery.refresh}
        isRefreshing={cppQuery.isRefreshing}
        onApply={async (cppId) => {
          const creative = cppQuery.data?.rows.find(
            (c) => (c.product_page_id ?? "default") === cppId
          );
          if (!creative) {
            return {
              ok: false as const,
              error: "Pick a Custom Product Page first.",
            };
          }
          reportBulkAction({
            resource: "campaign",
            action: "apply_cpp",
            count: selectedIds.length,
            details: {
              creative_id: creative.creative_id,
              creative_name: creative.name,
            },
          });
          try {
            const res = await bulkApplyCpp.mutateAsync({
              campaign_ids: selectedIds,
              creative_id: creative.creative_id,
              creative_name: creative.name,
              // Carry the productPageId UUID + asa adamId so the
              // backend can mint a Creative wrapper on-the-fly when
              // creative_id is 0 (freshly-created CPP that Apple's
              // sync hasn't auto-registered yet).
              product_page_id: creative.product_page_id ?? null,
              asa_app_id: asaAppId,
            });
            if (res.failed.length === 0) {
              flashToast("CPP applied");
              return { ok: true as const };
            }
            // Partial success — keep modal open so the user sees the
            // failure list, and surface a red toast with the count.
            flashToast(
              `Applied to ${res.applied} ad group${res.applied === 1 ? "" : "s"}; ${res.failed.length} failed`,
              "error"
            );
            return {
              ok: false as const,
              error: res.failed
                .slice(0, 3)
                .map((f) => `Ad group ${f.ad_group_id}: ${f.error}`)
                .join("\n"),
            };
          } catch (err) {
            const msg =
              (err as { response?: { data?: { error?: { message?: string } } } })
                ?.response?.data?.error?.message ?? "Couldn't apply CPP";
            flashToast(msg, "error");
            return { ok: false as const, error: msg };
          }
        }}
      />
      <RulesManagerModal
        open={rulesManagerOpen}
        onClose={() => setRulesManagerOpen(false)}
        entityType="campaign"
        entityIds={selectedIds}
        hasMaxConv={hasMaxConvSelected}
        appId={selectedAppId}
        onCreateNew={() => {
          setRulesManagerOpen(false);
          navigate("/rules?create=1");
        }}
      />
      {toast && (
        <StatusToast
          message={toast.msg}
          variant={toast.variant}
          action={toast.action}
        />
      )}

      {statusConfirm && (
        <ConfirmDialog
          open
          title={
            statusConfirm.target === "PAUSED"
              ? `Pause ${statusConfirm.ids.length} campaigns?`
              : `Enable ${statusConfirm.ids.length} campaigns?`
          }
          body={
            statusConfirm.target === "PAUSED"
              ? "This will stop delivery on all selected campaigns. They'll keep their settings, you can re-enable later. Sometimes Pause is irreversible from a delivery standpoint (slot loss), so double-check the count."
              : `This will resume delivery on ${statusConfirm.ids.length} selected campaigns. Daily budgets remain as configured.`
          }
          confirmLabel={statusConfirm.target === "PAUSED" ? "Pause all" : "Enable all"}
          destructive={statusConfirm.target === "PAUSED"}
          onCancel={() => setStatusConfirm(null)}
          onConfirm={() => {
            const { target, ids } = statusConfirm;
            setStatusConfirm(null);
            void runBulkStatus(target, ids);
          }}
        />
      )}
    </LabelsProvider>
  );
}

// Build URL params for navigating to a campaign detail page so the
// period filter (preset or custom range) carries over. We mirror the
// param semantics that useAdsFilters reads on the destination page:
// `preset` always (omitted when default), `from`/`to` only when the
// preset is `custom`.
function rowLinkParams(filters: ReturnType<typeof useAdsFilters>[0]): URLSearchParams {
  const out = new URLSearchParams();
  if (filters.preset !== "7d") out.set("preset", filters.preset);
  if (filters.mode === "static") out.set("mode", "static");
  if (filters.preset === "custom" || filters.mode === "static") {
    out.set("from", ymd(filters.range.from));
    out.set("to", ymd(filters.range.to));
  }
  if (filters.appId !== "all") out.set("app", String(filters.appId));
  return out;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// ---------------------------------------------------------------------------
// Enable / Pause buttons. Always visible in the toolbar so the
// screen doesn't reflow when you tick a checkbox — inactive when no
// row is selected, active with a count badge once something is.
// Delete intentionally dropped from the UI — bulk destruction of
// campaigns is not a workflow we want one click away.
// ---------------------------------------------------------------------------

// BulkActions extracted to components/BulkActions.tsx — used by both
// CampaignsPage (campaign grain) and CampaignDetailPage's AdGroups +
// Keywords tabs.

function isRealBundleId(v: string | null | undefined): v is string {
  if (!v) return false;
  // Collector seeds every new app with `asa-<adamId>` as a placeholder
  // because ASA v4 doesn't expose the real bundle id. Treat anything
  // matching that shape as "no real bundle id yet".
  return !/^asa-\d+$/.test(v);
}

function initialsFor(name: string): string {
  const parts = name
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (a + b).toUpperCase().slice(0, 2);
}

function tintFor(id: number): string {
  const palettes = [
    "linear-gradient(135deg,#6e62ff,#4a40cc)",
    "linear-gradient(135deg,#1f9d55,#0e7b45)",
    "linear-gradient(135deg,#c88407,#8d5e07)",
    "linear-gradient(135deg,#2f66f2,#1440a8)",
    "linear-gradient(135deg,#d64242,#b23030)",
    "linear-gradient(135deg,#5c6275,#2a2f3d)",
    "linear-gradient(135deg,#2fb46f,#139454)",
    "linear-gradient(135deg,#8d85ff,#5a4ff0)",
  ];
  return palettes[id % palettes.length] ?? palettes[0]!;
}
